/**
 * Black Marble — INTRO CINEMATIC timeline (pure math, no DOM, no audio side
 * effects). A faithful TypeScript re-port of the authoritative Claude Design
 * reference (intro-scenes.jsx): a 30-second, 5-beat opening (Ignition →
 * MarbleEdge → NamesWall → Dust → MarbleWide) resolving onto the carved
 * TitleLanding. Every export is a PURE function of the clock `t` (seconds,
 * 0..30) so the schedule can be unit tested without mounting anything — the
 * controller (introCinematic.ts) and the WebGL marble (introMarble.ts) read
 * these envelopes per frame, mirroring how the reference keyframed each scene.
 */

import * as THREE from 'three';

/** Total run length of the cinematic, in seconds. */
export const INTRO_DURATION_SEC = 30;

export type IntroBeatId =
  | 'ignition'
  | 'marbleEdge'
  | 'namesWall'
  | 'dust'
  | 'marbleWide'
  | 'titleLanding';

export type IntroAudioBus = 'music' | 'sfx' | 'ambience';

/** A single beat window plus its multi-stop opacity envelope (in seconds). */
export interface IntroBeat {
  id: IntroBeatId;
  start: number;
  end: number;
  /** Sorted keyframe times within `[start, end]`. */
  opacityStops: readonly number[];
  /** Opacity at each keyframe, 0..1, paired with `opacityStops`. */
  opacityVals: readonly number[];
}

/** One scheduled audio cue, mapped onto an engine sub-bus. */
export interface IntroAudioCue {
  id: string;
  file: string;
  bus: IntroAudioBus;
  start: number;
  gain: number;
  loop: boolean;
}

/** Per-layer render state the cinematic applies as inline styles each frame. */
export interface IntroLayerState {
  opacity: number;
  scale: number;
  translateY: number;
}

/** A normalised easing curve `[0,1] → [0,1]`. */
export type Easing = (t: number) => number;

/** Clamp `value` into the inclusive `[min, max]` range. */
export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

/** Two-point linear blend from `from` to `to` by a clamped `0..1` parameter. */
export const mix = (from: number, to: number, t: number): number =>
  from + (to - from) * clamp(t, 0, 1);

/** Identity easing — a clamped pass-through. */
export const linear: Easing = (t) => clamp(t, 0, 1);

/** Cubic ease-out — fast then settling (matches the reference `easeOutCubic`). */
export const easeOutCubic: Easing = (t) => {
  const x = clamp(t, 0, 1);
  return 1 - (1 - x) ** 3;
};

/** Cubic ease-in — slow then accelerating. */
export const easeInCubic: Easing = (t) => clamp(t, 0, 1) ** 3;

/** Symmetric cubic ease — a punchier default beat envelope shaping. */
export const easeInOutCubic: Easing = (t) => {
  const x = clamp(t, 0, 1);
  return x < 0.5 ? 4 * x ** 3 : 1 - (-2 * x + 2) ** 3 / 2;
};

/** Sine ease-out — the reference's soft landing for camera + opacity moves. */
export const easeOutSine: Easing = (t) => Math.sin((clamp(t, 0, 1) * Math.PI) / 2);

/** Symmetric sine ease — the reference's default crossfade shaping. */
export const easeInOutSine: Easing = (t) => -(Math.cos(Math.PI * clamp(t, 0, 1)) - 1) / 2;

/** Hermite smoothstep between two edges. */
export const smoothstep = (edge0: number, edge1: number, x: number): number => {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

/**
 * Multi-stop interpolation: walk the sorted `stops` keyframes and blend the
 * paired `vals` across the segment that contains `x`, shaping the in-segment
 * parameter with `easing`. Values outside the keyframe range clamp to the
 * nearest endpoint. This is the primitive every reference envelope is built
 * from (opacity ramps, marble zoom/tilt, letterbox retract).
 */
export const interpolate = (
  stops: readonly number[],
  vals: readonly number[],
  x: number,
  easing: Easing = linear,
): number => {
  const n = stops.length;
  if (n === 0) return 0;
  if (n === 1 || x <= stops[0]!) return vals[0]!;
  if (x >= stops[n - 1]!) return vals[n - 1]!;
  for (let i = 1; i < n; i++) {
    const lo = stops[i - 1]!;
    const hi = stops[i]!;
    if (x <= hi) {
      const local = hi === lo ? 1 : (x - lo) / (hi - lo);
      return mix(vals[i - 1]!, vals[i]!, easing(local));
    }
  }
  return vals[n - 1]!;
};

/**
 * The 5 narrative beats plus the carved title landing, laddered so each
 * crossfades into the next across the 30-second clock. Each beat carries its
 * opacity keyframes ported from the reference scene table: a beat reaches full
 * brightness exactly when its paired audio cue hits — ignition 0.8,
 * marbleEdge/emergence 4.2, namesWall/carve 11.0, dust/rake 12.8, marbleWide
 * ~17, titleLanding/title 23.4 (then held lit through 30 under the looping
 * drone bed). `beatOpacity` gates each envelope to its `[start, end]` window.
 */
// Windows restored to the ORIGINAL handoff values (Wave 1 parity — see
// intro-parity-wave1-input.md §2 "DOM fallback"); only Dust keeps its
// deliberate retime onto the carve/rake cues (spec §4).
export const INTRO_BEATS: readonly IntroBeat[] = [
  { id: 'ignition', start: 0, end: 11.5, opacityStops: [0, 1.5, 9.5, 11.5], opacityVals: [0, 1, 1, 0] },
  { id: 'marbleEdge', start: 3.6, end: 12.4, opacityStops: [3.6, 5.6, 10.5, 12.4], opacityVals: [0, 1, 1, 0] },
  { id: 'namesWall', start: 10.6, end: 19.4, opacityStops: [10.6, 12.4, 17.6, 19.4], opacityVals: [0, 1, 1, 0] },
  { id: 'dust', start: 11.3, end: 20, opacityStops: [11.3, 12.8, 18, 20], opacityVals: [0, 1, 1, 0] },
  { id: 'marbleWide', start: 17.4, end: 30, opacityStops: [17.4, 19.6, 30], opacityVals: [0, 1, 1] },
  { id: 'titleLanding', start: 21.8, end: 30, opacityStops: [21.8, 23.4, 30], opacityVals: [0, 1, 1] },
];

/**
 * The 8-file audio manifest, aligned to intro-audio-manifest.json: a
 * non-looping score on the music bus, a looping drone bed that holds under the
 * title (ambience bus), and six one-shots across the sfx / ambience buses.
 * Every `file` matches an asset on disk under /assets/audio; starts/gains/loops
 * are the schedule introAudio.ts plays. Restrained gains keep the one-shots
 * under the score (music 0.85, sfx/ambience 0.6–0.9).
 */
export const INTRO_AUDIO_CUES: readonly IntroAudioCue[] = [
  { id: 'score', file: 'intro-score', bus: 'music', start: 0, gain: 0.85, loop: false },
  { id: 'ignition', file: 'intro/sfx-intro-ignition', bus: 'ambience', start: 0.8, gain: 0.8, loop: false },
  { id: 'sliver', file: 'intro/sfx-intro-sliver', bus: 'sfx', start: 2.6, gain: 0.7, loop: false },
  { id: 'emergence', file: 'intro/sfx-intro-emergence', bus: 'sfx', start: 4.2, gain: 0.75, loop: false },
  { id: 'carve', file: 'intro/sfx-intro-carve', bus: 'ambience', start: 11, gain: 0.6, loop: false },
  { id: 'rake', file: 'intro/sfx-intro-rake', bus: 'sfx', start: 12.8, gain: 0.7, loop: false },
  { id: 'title', file: 'intro/sfx-intro-title', bus: 'sfx', start: 23.4, gain: 0.9, loop: false },
  { id: 'drone', file: 'intro/sfx-intro-drone', bus: 'ambience', start: 25.5, gain: 0.6, loop: true },
];

const beatById = (id: IntroBeatId): IntroBeat => {
  const beat = INTRO_BEATS.find((b) => b.id === id);
  if (!beat) throw new Error(`unknown intro beat: ${id}`);
  return beat;
};

/**
 * Opacity envelope for a beat: 0 outside `[start, end]`, otherwise the beat's
 * multi-stop reference keyframes shaped by a sine crossfade. A beat with a
 * trailing `1` value (the title landing) rests lit through its `end` (t = 30).
 */
export const beatOpacity = (id: IntroBeatId, t: number): number => {
  const beat = beatById(id);
  if (t < beat.start || t > beat.end) return 0;
  return clamp(interpolate(beat.opacityStops, beat.opacityVals, t, easeInOutSine), 0, 1);
};

/** True once cue `start` has been reached (used to fire one-shots on schedule). */
export const cueActiveAt = (cue: IntroAudioCue, t: number): boolean => t >= cue.start;

/** Per-beat render state (opacity + a gentle parallax push) at clock `t`. */
export const beatState = (id: IntroBeatId, t: number): IntroLayerState => {
  const beat = beatById(id);
  const span = beat.end - beat.start;
  const local = span > 0 ? clamp((t - beat.start) / span, 0, 1) : 0;
  return {
    opacity: beatOpacity(id, t),
    scale: mix(1.06, 1, easeOutCubic(local)),
    translateY: mix(2, -2, easeInOutCubic(local)),
  };
};

/** Starfield drift parallax — a slow downward pan over the full run. */
export const starfieldDrift = (t: number): number =>
  mix(0, -6, clamp(t / INTRO_DURATION_SEC, 0, 1));

/** Letterbox bar height (design px) at full thickness while the film plays. */
export const LETTERBOX_BAR_PX = 138;

/**
 * Letterbox retract: the cinematic bars hold at {@link LETTERBOX_BAR_PX} until
 * t = 23.6, then ease away to 0 by t = 25.4 as the title lands — the
 * "[138, 0] over [23.6, 25.4]" handoff that opens the frame for the live
 * TitleScreen.
 */
export const letterboxBarHeight = (t: number): number =>
  // easeInOutCubic per the original handoff (C¹: zero velocity at both ends).
  interpolate([23.6, 25.4], [LETTERBOX_BAR_PX, 0], t, easeInOutCubic);

/**
 * Twinkle alpha for a single star: its `base` brightness modulated by a sine
 * keyed to a per-star `phase` and `speed` so the 190-star field shimmers
 * deterministically without Math.random.
 */
export const twinkleAlpha = (
  base: number,
  phase: number,
  speed: number,
  t: number,
): number => {
  const osc = (Math.sin(t * speed + phase * Math.PI * 2) + 1) / 2;
  return clamp(base * (0.4 + 0.6 * osc), 0, 1);
};

export interface MoteState {
  x: number;
  y: number;
  opacity: number;
}

/**
 * Drift state for one dust mote: a slow upward rise (wrapped to `[0,1]`) with a
 * gentle horizontal sway, brightness from the seed depth. Pure so the 38-mote
 * field is reproducible and unit-testable.
 */
export const moteState = (p: IntroPoint, t: number): MoteState => {
  const raw = (p.y - t * 0.012) % 1;
  const y = raw < 0 ? raw + 1 : raw;
  const sway = Math.sin(t * 0.6 + p.z * Math.PI * 2) * 0.04;
  return {
    x: clamp(p.x + sway, 0, 1),
    y,
    opacity: clamp(0.18 + p.z * 0.5, 0, 1),
  };
};

/**
 * Boot gate: whether the cinematic should play for a given `location.search`.
 * The intro runs on EVERY cold boot / return-to-title (no sessionStorage gate)
 * and is suppressed only by an explicit `?intro=skip`; any other value (e.g.
 * `?intro=force`) still plays. Pure so the boot wiring stays unit-testable.
 */
export const shouldPlayIntro = (search: string): boolean =>
  new URLSearchParams(search).get('intro') !== 'skip';

export interface IntroPoint {
  x: number;
  y: number;
  z: number;
}

/**
 * Deterministic LCG point cloud (Numerical Recipes constants). Each draw is
 * `s = (s * 1664525 + 1013904223) >>> 0` normalised to `0..1`, so the starfield
 * and the names wall lay out identically on every cold boot — no Math.random.
 */
export const introPointCloud = (seed: number, count: number): IntroPoint[] => {
  let s = seed >>> 0;
  const next = (): number => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
  const points: IntroPoint[] = [];
  for (let i = 0; i < count; i++) {
    points.push({ x: next(), y: next(), z: next() });
  }
  return points;
};

/** Layout seed for the carved names grid (the reference `0xb17e`). */
export const NAME_SEED = 0xb17e;

/** Total carved-souls prop, from the reference (`Black Marble Intro.dc.html`). */
export const NAMES_CARVED_TOTAL = 1_247_309_544;

/** The reference top-right label, e.g. "1,247,309,544 NAMES CARVED". */
export const formatNamesCarved = (count: number = NAMES_CARVED_TOTAL): string =>
  `${count.toLocaleString('en-US')} ${count === 1 ? 'NAME' : 'NAMES'} CARVED`;

/**
 * The 120-name pool carved into the wall beat. A literal roster of the lost —
 * laid out across the perspective grid via `introPointCloud(NAME_SEED, …)` so
 * the same names land in the same cells on every boot.
 */
export const NAME_POOL: readonly string[] = [
  'ANNELISE VOSS', 'KORR DELANEY', 'MIRA OKONKWO', 'SETH HALVARD', 'IVO PRASAD', 'LENA CAIRN',
  'OSWIN TANAKA', 'BRYCE MALLOW', 'NADIA SERR', 'EDMUND FOLEY', 'PERA QUILL', 'THANE OERSTED',
  'ELIAS NWOSU', 'RHEA VANTOR', 'COLM ASHBY', 'YUKI MORENO', 'DARIUS KANE', 'ISLA FENWICK',
  'TOBIAS REIN', 'AMARA SOLIS', 'GIDEON HOLT', 'NEVE LARSSON', 'RANA ABBASI', 'PETROV MIKKEL',
  'SAOIRSE BELL', 'HANNO WEISS', 'LIORA STARK', 'MATEO RIVAS', 'FREYA NOLAN', 'JUDE OKAFOR',
  'VESNA POLAK', 'ARLO HENSHAW', 'MAEVE DORAN', 'KAITO ISHII', 'NORA BELLAMY', 'SILAS VANCE',
  'OPAL TREMAINE', 'EZRA CALLOW', 'ANYA VOLKOV', 'DESMOND IGE', 'LUCIA FERRO', 'OMAR HADID',
  'BRIDGET KANE', 'THEO MARSH', 'ELODIE RAVN', 'NIKO PETRAS', 'SABLE WREN', 'HUGO ALMASI',
  'CARYS LOWE', 'RAFAEL MUNOZ', 'INES VARGA', 'BOWEN TATE', 'MIRELA DACO', 'AKSEL BRANDT',
  'PHOEBE DARROW', 'KENJI ARAI', 'NOA HALSTEAD', 'TARA VESPER', 'LEON CASTELL', 'RUNE EIDE',
  'MARISOL DUNE', 'EVREN BAYAT', 'CLEO HARGREAVE', 'IDRIS FALK', 'JONA MERETT', 'SVEA LIND',
  'CASPIAN ORR', 'NELLA FORTH', 'GARETH OYELE', 'TAMSIN ROOK', 'VLAD ANTONOV', 'MEI LANCASTER',
  'ROHAN BELLOW', 'ASTRID VOSE', 'MILO HARTNETT', 'ZARA NIELD', 'FINN ABERNATHY', 'LARA OSWALT',
  'DECLAN ROSS', 'YASMIN COLE', 'PAVEL DROZD', 'ENID HALE', 'OSCAR REINHOLT', 'NURI ADAMS',
  'BRIAR KESTREL', 'EVAN TOLLAND', 'MARA SVENSK', 'KASIM REYES', 'LOTTE VERMEER', 'ARNE HOLLOW',
  'SADIE QUINTON', 'REMY BASTIEN', 'ILSE HOFFER', 'TOMAS LEDOUX', 'GRETA MOSS', 'ADIL NAVARRO',
  'BENEDICT FAY', 'ORLA SHANE', 'KIRAN BHATT', 'MAREN FALK', 'GIANNI ROSSO', 'ELSIE BARRON',
  'HOLT MERCER', 'ANAIS DUBOIS', 'TEAGAN VOSS', 'RAUL CASTELLO', 'MINNA EKHOLM', 'OBIE TRENT',
  'SERENA KOVAC', 'ALDOUS PRYNE', 'NIAMH CARROL', 'OTTO FREDRIK', 'LUMEN BLAISE', 'CADE WHITLOCK',
  'VERA SOLANO', 'JASPER NEEL', 'ROWAN ELLISON', 'MAYA DELACROIX', 'EINAR HOLM', 'PRIYA SETHI',
];

// ===========================================================================
// IN-ENGINE CINEMATIC CAMERA SPLINE  (authoritative 8-keyframe path)
// ---------------------------------------------------------------------------
// World origin = cenotaph centre. 1 unit = 1 km. +X right, +Y up, +Z toward the
// default viewer. `fov` is vertical degrees; `roll` is dutch (about local forward,
// applied AFTER lookAt). Drive `t` from the same CineClock the DOM overlay + audio
// read so engine, DOM beats, and cues stay locked. Pure math — no WebGL, unit-test
// the path by sampling a bare PerspectiveCamera.
// ===========================================================================

/**
 * Direction FROM centre TOWARD the sun, unit. Re-aimed to a FRONT/UPPER-RIGHT key
 * (+Z = toward the viewer) so the camera-facing hemisphere READS as a lit monument —
 * matching the reference `shots/04-beat.png`, where the marble is a softly side-lit
 * sphere, NOT the pure back-lit silhouette the first cut shipped (which reproduced the
 * original "near-invisible marble" defect). The Fresnel rim now catches the visible limb.
 */
export const SUN_DIR = new THREE.Vector3(0.36, 0.5, -0.79).normalize();

/** Cenotaph cinematic body-rotation rate (rad/s) — reverent, ~17.5 min/rev, ALIVE. */
export const CINE_ROT_RATE = 0.006;
/** Phase pre-rotation so the densest inscription band faces the terminator at t≈14s. */
export const CINE_ROT_PHASE = -0.34;

/** One camera keyframe: pose + lens + the easing applied across the segment ENDING here. */
export interface CamKey {
  t: number;
  pos: THREE.Vector3;
  look: THREE.Vector3;
  fov: number;
  roll: number;
  ease: Easing;
}

const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

/** The authoritative 8-keyframe camera path (Cinematography spline, framed to SUN_DIR). */
export const INTRO_CAMERA_KEYS: readonly CamKey[] = [
  // Ignition = void-dominant: camera looks UP toward the cold sun so the marble sits
  // below/out of frame; only the sun + raking sliver read in the upper dark.
  { t: 0.0, pos: V(2, -16, 360), look: V(46, 95, -54), fov: 34, roll: -1.5, ease: easeInOutSine },
  { t: 4.5, pos: V(8, -24, 300), look: V(42, 80, -44), fov: 34, roll: -1.6, ease: easeInOutSine },
  // Emergence = "planetrise": camera low, looking UP toward the cold sun so the marble's
  // lit top limb arcs across the LOWER frame (centre below the bottom edge), sun above.
  { t: 8.0, pos: V(16, -58, 198), look: V(26, 70, 10), fov: 38, roll: -2.0, ease: easeInOutSine },
  { t: 12.0, pos: V(40, -44, 142), look: V(38, 44, 16), fov: 40, roll: -1.6, ease: easeInOutSine },
  { t: 14.0, pos: V(67.3, 3.9, 39.2), look: V(74, 3, -2), fov: 41, roll: 2.5, ease: easeOutCubic },
  { t: 18.0, pos: V(77.7, 4.1, 29.1), look: V(83, 6, -12), fov: 41, roll: 1.5, ease: easeInOutSine },
  // Marble reveal pulled back + reached EARLIER (wide by ~t21, like the reference) and
  // framed sphere RIGHT-of-centre (look left of origin) so the left holds negative space.
  { t: 21.0, pos: V(150, 44, 270), look: V(-34, -4, 0), fov: 36, roll: 0.4, ease: easeInOutSine },
  { t: 30.0, pos: V(132, 56, 300), look: V(-36, -7, 0), fov: 36, roll: 0.0, ease: easeOutSine },
];

/** The resting establishing pose (KF7) the TitleScreen inherits at handoff. */
export const INTRO_HANDOFF_KEY: CamKey = INTRO_CAMERA_KEYS[INTRO_CAMERA_KEYS.length - 1]!;

const DEG = Math.PI / 180;
const CAM_SEGMENTS = INTRO_CAMERA_KEYS.length - 1;

// Catmull-Rom curves THROUGH the keyframe poses give C1-continuous (no-corner) camera
// motion — the plain per-segment lerp the first cut used snapped direction at every KF,
// which read as a jerky, un-cinematic dolly. Centripetal (0.5) parameterisation avoids
// overshoot/cusps on the tighter Names→Marble keys.
const POS_CURVE = new THREE.CatmullRomCurve3(
  INTRO_CAMERA_KEYS.map((k) => k.pos.clone()),
  false,
  'centripetal',
  0.5,
);
const LOOK_CURVE = new THREE.CatmullRomCurve3(
  INTRO_CAMERA_KEYS.map((k) => k.look.clone()),
  false,
  'centripetal',
  0.5,
);
const camPosScratch = new THREE.Vector3();
const camLookScratch = new THREE.Vector3();

/**
 * Pure: sample the camera pose at cinematic time `t` (seconds), mutating `cam` in
 * place. Position + lookAt ride Catmull-Rom splines through the keyframes (smooth, no
 * corners); the per-segment easing shapes the in-segment parameter so the pacing/holds
 * are preserved; fov/roll lerp linearly; the dutch roll is applied AFTER lookAt.
 */
export const sampleIntroCamera = (cam: THREE.PerspectiveCamera, t: number): void => {
  const keys = INTRO_CAMERA_KEYS;
  let i = 0;
  if (t <= keys[0]!.t) {
    i = 0; // before the first keyframe → clamp to KF0
  } else if (t >= keys[CAM_SEGMENTS]!.t) {
    i = CAM_SEGMENTS - 1; // after the last keyframe → clamp to KF7
  } else {
    for (i = 0; i < CAM_SEGMENTS; i++) {
      if (t >= keys[i]!.t && t <= keys[i + 1]!.t) break;
    }
  }
  const a = keys[i]!;
  const b = keys[i + 1]!;
  const span = b.t - a.t;
  const e = b.ease(span > 0 ? clamp((t - a.t) / span, 0, 1) : 1);
  const u = (i + e) / CAM_SEGMENTS; // global curve parameter with per-segment easing
  POS_CURVE.getPoint(u, camPosScratch);
  LOOK_CURVE.getPoint(u, camLookScratch);
  cam.position.copy(camPosScratch);
  cam.up.set(0, 1, 0);
  cam.lookAt(camLookScratch);
  cam.rotateZ((a.roll + (b.roll - a.roll) * e) * DEG); // dutch about forward, AFTER lookAt
  cam.fov = a.fov + (b.fov - a.fov) * e;
  cam.updateProjectionMatrix();
};

// Scratch cameras for the 24fps blend (module-private, never rendered).
const quantA = new THREE.PerspectiveCamera();
const quantB = new THREE.PerspectiveCamera();

/**
 * The "24fps deliberate" cadence WITHOUT 3:2-pulldown judder: sample the spline at
 * a 24fps-quantised clock (`floor(t*24)/24` and `+1/24`) and lerp by the sub-frame
 * fraction, while the render loop itself runs at full refresh. Gives the filmic
 * 24fps motion cadence judder-free. Mutates `cam` in place.
 */
export const sampleIntroCamera24fps = (cam: THREE.PerspectiveCamera, t: number): void => {
  const FPS = 24;
  const frame = Math.floor(t * FPS);
  const t0 = frame / FPS;
  const t1 = (frame + 1) / FPS;
  const frac = clamp((t - t0) / (t1 - t0), 0, 1);
  // Sample both quantised frames into scratch cameras, then lerp pose + lens.
  sampleIntroCamera(quantA, t0);
  sampleIntroCamera(quantB, t1);
  cam.position.lerpVectors(quantA.position, quantB.position, frac);
  cam.quaternion.copy(quantA.quaternion).slerp(quantB.quaternion, frac);
  cam.fov = quantA.fov + (quantB.fov - quantA.fov) * frac;
  cam.updateProjectionMatrix();
};

// ---------------------------------------------------------------------------
// Per-beat grade envelopes (pure, deterministic — read off the CineClock).
// ---------------------------------------------------------------------------

/**
 * ACES exposure ramp. Lifted well above the first cut's 0.62→0.72 (which crushed the
 * matte obsidian to invisible): the void still admits light slowly, but the monument
 * READS — ~0.85 (Ignition) → ~1.0 (Names) → ~1.05 (Marble/Title).
 */
export const introExposure = (t: number): number =>
  interpolate([0, 4.5, 8, 17.5, 23], [1.0, 1.02, 1.08, 1.08, 1.1], t, easeInOutSine);

/**
 * Fresnel rim strength per beat. Strong on the Emergence hero limb, then DROPPED low for
 * the wide Marble/Title — the reference shows only a subtle cold crescent on the lower
 * limb there, not a bright ring wrapping the whole sphere.
 */
export const introRimStrength = (t: number): number =>
  interpolate([0, 8, 14, 23, 30], [1.1, 1.5, 1.0, 0.42, 0.42], t, easeInOutSine);

/** Heavy filmic grain easing 0.05 → 0.08 over the 30s (never exceeds 0.09 — blacks crawl). */
export const introGrain = (t: number): number =>
  interpolate([0, 30], [0.05, 0.08], t, linear);

/**
 * Visible cold-sun disc opacity — original handoff kindle: dark until 0.8 (the
 * ignition cue), full by 2.4, holds through Emergence, fades 9.5→11.5.
 */
export const introSunOpacity = (t: number): number =>
  interpolate([0.8, 2.4, 9.5, 11.5], [0, 1, 1, 0], t, easeOutCubic);

/**
 * Ignition flicker (reference): the kindling sun stutters `0.6 + 0.4·sin(t·34)`
 * while t < 2.4, then burns steady. Multiplies {@link introSunOpacity}.
 */
export const introSunFlicker = (t: number): number =>
  t < 2.4 ? 0.6 + 0.4 * Math.sin(t * 34) : 1;

/** BokehPass DOF aperture per beat (deepest focus at the Names graze). */
export const introAperture = (t: number): number =>
  interpolate([0, 8, 14, 18, 23, 30], [0.00003, 0.00006, 0.00013, 0.00013, 0.00002, 0.00002], t, easeInOutSine);

/** BokehPass DOF max-blur per beat (strongest at the Names graze). */
export const introMaxBlur = (t: number): number =>
  interpolate([0, 8, 14, 18, 23, 30], [0.005, 0.009, 0.014, 0.014, 0.004, 0.004], t, easeInOutSine);
